import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';

// Import components
import Header from './components/Header';
import Home from './components/Home';
import About from './components/About';
import Founder from './components/Founder';
import Programs from './components/Programs';
import Facilities from './components/Facilities';
import Gallery from './components/Gallery';
import News from './components/News';
import Contact from './components/Contact';
import Achievements from './components/Achievements';
import WhyChooseUs from './components/WhyChooseUs';
import Testimonials from './components/Testimonials';
import Footer from './components/Footer';
import ScrollIndicator from './components/ScrollIndicator';
import AnnouncementModal from './components/AnnouncementModal';
import { MatchExperience, AuthPanel } from './components/Matches';
import { AuthProvider, useAuth } from './context/AuthContext';

// Import announcement data
import announcementsData from './data/announcements.json';

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [theme, setTheme] = useState('dark');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [announcements, setAnnouncements] = useState([]);

  // Theme toggle functionality
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Load announcements on app start
  useEffect(() => {
    console.log('Loading announcements data:', announcementsData);
    setAnnouncements(announcementsData);
    // Show announcements after a short delay to ensure app is loaded
    const timer = setTimeout(() => {
      console.log('Setting showAnnouncements to true');
      setShowAnnouncements(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const openAuth = () => setAuthOpen(true);
    window.addEventListener('bnioc-open-auth', openAuth);
    return () => window.removeEventListener('bnioc-open-auth', openAuth);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname, location.search]);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // Mobile menu toggle
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Handle announcement modal close
  const handleAnnouncementClose = () => {
    setShowAnnouncements(false);
  };

  return (
    <div className="App min-h-screen bg-white dark:bg-secondary-900 transition-colors duration-300">
        <Header 
          theme={theme} 
          toggleTheme={toggleTheme} 
          isMenuOpen={isMenuOpen} 
          toggleMenu={toggleMenu} 
          onSignIn={() => setAuthOpen(true)}
        />
        {authOpen && <AuthPanel onSuccess={(result) => { setUser(result); setAuthOpen(false); if (result.user.role === 'admin') navigate('/matches?admin=1'); }} onClose={() => setAuthOpen(false)} />}
        
        <main className="pt-16 lg:pt-20">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/founder" element={<Founder />} />
            <Route path="/programs" element={<Programs />} />
            <Route path="/facilities" element={<Facilities />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/news" element={<News />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/why-choose-us" element={<WhyChooseUs />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/matches" element={<MatchExperience />} />
            <Route path="/matches/:slug" element={<MatchExperience />} />
          </Routes>
        </main>
        
        {!location.pathname.startsWith('/matches') && <Footer />}
        
        {/* Global Scroll Indicator */}
        <ScrollIndicator />
        
        {/* Announcement Modal */}
        {showAnnouncements && location.pathname === '/' && (
          <AnnouncementModal 
            announcements={announcements}
            onClose={handleAnnouncementClose}
          />
        )}
      </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
